import { useState } from "react";
import { Copy, ExternalLink, MapPin, Navigation } from "lucide-react";

import { Button } from "../ui/button";
import { toast } from "react-toastify";
import { quadradosDoMapa } from "../../utils/localizacao";

// O cartão de localização da conversa.
//
// Antes o "Visualizar" abria o que estivesse na segunda parte do corpo da
// mensagem — que, no formato que o WhatsApp gravava, era a DESCRIÇÃO e não o
// link. O navegador tratava "Rua tal, 123" como endereço relativo, saía do
// atendimento e caía numa tela de erro. Agora só abre o que é http(s), e o
// resto do cartão mostra o que a pessoa mandou: mapa, nome, endereço e as
// coordenadas.
const LocationPreview = ({
	image,
	link,
	description,
	latitude,
	longitude,
}) => {
	const [mapaFalhou, setMapaFalhou] = useState(false);

	const linkSeguro = /^https?:\/\//i.test(link || "") ? link : "";
	const temCoordenadas =
		Number.isFinite(latitude) && Number.isFinite(longitude);

	const [titulo, ...restoDaDescricao] = String(description || "")
		.split(/\\n|\n/)
		.map((linha) => linha.trim())
		.filter(Boolean);

	const abrirMapa = () => {
		if (!linkSeguro) return;
		window.open(linkSeguro, "_blank", "noopener,noreferrer");
	};

	const copiarCoordenadas = async () => {
		const texto = temCoordenadas
			? `${latitude}, ${longitude}`
			: linkSeguro;

		try {
			await navigator.clipboard.writeText(texto);
			toast.success("Coordenadas copiadas.");
		} catch {
			toast.error("Não foi possível copiar.");
		}
	};

	return (
		<div className="w-[260px] max-w-full overflow-hidden rounded-lg border bg-card">
			<button
				type="button"
				onClick={abrirMapa}
				disabled={!linkSeguro}
				className="relative block h-[130px] w-full bg-muted"
				aria-label="Abrir localização no mapa"
				title={linkSeguro ? "Abrir no Google Maps" : undefined}
			>
				{image ? (
					<img
						src={image}
						alt="Prévia da localização"
						className="h-full w-full object-cover"
					/>
				) : temCoordenadas && !mapaFalhou ? (
					/* Sem miniatura do WhatsApp, o mapa é montado com os quadrados
					   do OpenStreetMap — imagens, sem chave de API e sem os
					   controles do mapa embutido por cima do cartão. */
					<span className="absolute inset-0 block overflow-hidden">
						{quadradosDoMapa(latitude, longitude).map((quadrado) => (
							<img
								key={quadrado.chave}
								src={quadrado.url}
								alt=""
								loading="lazy"
								decoding="async"
								onError={() => setMapaFalhou(true)}
								className="absolute h-64 w-64 max-w-none"
								style={{ left: quadrado.left, top: quadrado.top }}
							/>
						))}

						{/* O alfinete fica no centro: é para lá que os quadrados
						    foram deslocados. */}
						<MapPin className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-full fill-red-500 text-white drop-shadow" />

						<span className="absolute bottom-0 right-0 bg-background/80 px-1 text-[9px] text-muted-foreground">
							© OpenStreetMap
						</span>
					</span>
				) : (
					<span className="flex h-full w-full items-center justify-center">
						<MapPin className="h-7 w-7 text-muted-foreground" />
					</span>
				)}

				<span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-[11px] font-medium shadow-xs">
					<MapPin className="h-3 w-3 text-red-500" />
					Localização
				</span>
			</button>

			<div className="space-y-0.5 px-3 py-2">
				<p className="truncate text-sm font-medium" title={titulo}>
					{titulo || "Localização compartilhada"}
				</p>

				{restoDaDescricao.length > 0 && (
					<p className="whitespace-pre-line text-xs text-muted-foreground">
						{restoDaDescricao.join("\n")}
					</p>
				)}

				{temCoordenadas && (
					<p className="font-mono text-[11px] text-muted-foreground">
						{Number(latitude).toFixed(6)}, {Number(longitude).toFixed(6)}
					</p>
				)}
			</div>

			<div className="flex border-t">
				<Button
					variant="ghost"
					size="sm"
					className="flex-1 gap-1 rounded-none text-xs"
					onClick={abrirMapa}
					disabled={!linkSeguro}
				>
					<Navigation className="h-3.5 w-3.5" />
					Abrir no mapa
				</Button>

				<Button
					variant="ghost"
					size="sm"
					className="gap-1 rounded-none border-l px-3 text-xs"
					onClick={copiarCoordenadas}
					title="Copiar coordenadas"
					aria-label="Copiar coordenadas"
				>
					<Copy className="h-3.5 w-3.5" />
				</Button>

				{linkSeguro && (
					<a
						href={linkSeguro}
						target="_blank"
						rel="noopener noreferrer"
						title="Abrir em nova aba"
						aria-label="Abrir em nova aba"
						className="flex items-center border-l px-3 text-muted-foreground hover:text-foreground"
					>
						<ExternalLink className="h-3.5 w-3.5" />
					</a>
				)}
			</div>
		</div>
	);
};

export default LocationPreview;
